/**
 * AuthGuard — root-level auth gate for the admin SPA.
 *
 * On mount:
 *   - If the accessToken cookie is present → render children.
 *   - If absent → redirect to the central login at
 *     www.knowledgegate.ai/auth/login?returnTo=<this URL>.
 *
 * Note: cookie presence is a necessary but not sufficient check — a
 * stale/revoked token still lets the user render the app but every API
 * call will 401, which triggers the redirect via the axios interceptor.
 * This double-guard is intentional: we want *immediate* redirect for
 * users with no cookie at all (first-time visitors, post-logout) but
 * lazy redirect for stale-token edge cases (don't want to race the cookie
 * read against a slow API response).
 */

import { useEffect, type ReactNode } from "react";
import { hasAuthCookie } from "@/lib/auth/cookies";
import { redirectToLogin } from "@/lib/auth/redirect";

export function AuthGuard({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (!hasAuthCookie()) {
      redirectToLogin();
    }
  }, []);

  if (!hasAuthCookie()) {
    // Briefly shown before the redirect fires. Keep the shell intact so
    // we don't flash "404" or a bare HTML background.
    return (
      <div className="flex min-h-full items-center justify-center p-8">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Redirecting to login…
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
